<script setup>
// Barra inferior tipo WhatsApp para celular (Victor 2026-06-02 · Propuesta 4).
// Permite saltar entre Chats / Leads / Tareas / Inicio / Informes con el pulgar,
// sin tocar el switcher de sucursal (que sigue arriba). Solo se monta en móvil
// (< 768px) y se oculta cuando hay un chat abierto a pantalla completa.
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { emitter } from 'shared/helpers/mitt';
import TasksAPI from 'dashboard/api/tasks';

const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

// Chats y Tareas comparten la misma ruta ('home'), así que recordamos cuál se
// tocó por última vez para resaltar el botón correcto.
const manualKey = ref(null);

const isChatsRoute = computed(() => {
  const n = route.name || '';
  return n === 'home' || !!route.params.conversation_id || /conversation/i.test(n);
});

const activeKey = computed(() => {
  const n = (route.name || '').toLowerCase();
  if (n.includes('pipeline')) return 'leads';
  if (n === 'home_blank') return 'inicio';
  if (n.includes('report') || n.includes('metric') || n.includes('overview')) {
    return 'informes';
  }
  if (isChatsRoute.value) return manualKey.value === 'tareas' ? 'tareas' : 'chats';
  return manualKey.value;
});

// Al salir de la ruta de Chats, soltamos el manualKey para que el resaltado lo
// determine la ruta nueva.
watch(
  () => route.name,
  () => {
    if (!isChatsRoute.value) manualKey.value = null;
  }
);

const goChats = () => {
  manualKey.value = 'chats';
  router.push(accountScopedRoute('home')).catch(() => {});
};
const goLeads = () => {
  manualKey.value = null;
  router.push(accountScopedRoute('pipelines_home')).catch(() => {});
};
const goInicio = () => {
  manualKey.value = null;
  router.push(accountScopedRoute('home_blank')).catch(() => {});
};
const goInformes = () => {
  manualKey.value = null;
  router.push(accountScopedRoute('advanced_metrics_reports')).catch(() => {});
};
const goTareas = () => {
  manualKey.value = 'tareas';
  router
    .push(accountScopedRoute('home'))
    .catch(() => {})
    .finally(() => {
      nextTick(() => emitter.emit('switch-assignee-tab', 'tasks'));
    });
};

// Badge de tareas pendientes (mismo origen que la campana del sidebar).
const tasksCount = ref(0);
let timer = null;
const fetchTasksCount = async () => {
  try {
    const { data: p1 } = await TasksAPI.list({ status: 'pendiente' });
    const { data: p2 } = await TasksAPI.list({ status: 'en_proceso' });
    tasksCount.value =
      (p1?.data?.payload?.length || 0) + (p2?.data?.payload?.length || 0);
  } catch (_) {
    /* silencioso */
  }
};
onMounted(() => {
  fetchTasksCount();
  timer = setInterval(fetchTasksCount, 60000);
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
});

const items = computed(() => [
  { key: 'chats', label: 'Chats', icon: 'i-lucide-message-circle', onClick: goChats },
  { key: 'leads', label: 'Leads', icon: 'i-lucide-target', onClick: goLeads },
  { key: 'tareas', label: 'Tareas', icon: 'i-lucide-list-checks', onClick: goTareas, badge: tasksCount.value },
  { key: 'inicio', label: 'Inicio', icon: 'i-lucide-house', onClick: goInicio },
  { key: 'informes', label: 'Informes', icon: 'i-lucide-chart-spline', onClick: goInformes },
]);
</script>

<template>
  <nav
    class="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around h-14 border-t border-n-weak bg-n-background/95 backdrop-blur-lg"
  >
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      class="relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-medium transition-colors"
      :class="
        activeKey === item.key
          ? 'text-[#c9a35a]'
          : 'text-n-slate-11 hover:text-n-slate-12'
      "
      @click="item.onClick"
    >
      <span :class="item.icon" class="text-[20px] leading-none" />
      <span>{{ item.label }}</span>
      <span
        v-if="item.badge"
        class="absolute top-1.5 right-[calc(50%-20px)] min-w-[15px] px-1 rounded-full bg-[#c9a35a] text-[#1a1610] text-[8px] font-extrabold leading-[15px] text-center"
      >{{ item.badge > 99 ? '99+' : item.badge }}</span>
    </button>
  </nav>
</template>
